import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CompetitionResultEntryClient, CompetitionResultEntryDTO, CompetitionResultEntryRankUpdateDTO } from '../services/apiClient';
import { API_BASE_URL, MAX_DROPDOWN_PAGE_SIZE } from '../config/config';
import axiosInstance from '../services/axiosInstance';

const apiClient = new CompetitionResultEntryClient(API_BASE_URL, axiosInstance);

// Direct API call (no caching) - persists a reordered Position/Rank for a batch of entries
export const useUpdateCompetitionResultEntryRanks = () => {
  const [isLoading, setIsLoading] = useState(false);

  const updateRanks = async (updates: CompetitionResultEntryRankUpdateDTO[]) => {
    setIsLoading(true);
    try {
      await apiClient.updateCompetitionResultEntryRanks(updates);
    } finally {
      setIsLoading(false);
    }
  };

  return { updateRanks, isLoading };
};

// Direct API call (no caching) - lightweight entries (exercise info only, no Score graph) for a result
export const useLightweightCompetitionResultEntries = () => {
  const [isLoading, setIsLoading] = useState(false);

  const getLightweightEntriesForResult = async (
    competitionResultId: string,
  ): Promise<CompetitionResultEntryDTO[]> => {
    setIsLoading(true);
    try {
      const response = await apiClient.getLightweightCompetitionResultEntriesForResult(competitionResultId);
      return (response.data as unknown as CompetitionResultEntryDTO[]) ?? [];
    } catch (err) {
      console.error('Error fetching lightweight competition result entries:', err);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  return { getLightweightEntriesForResult, isLoading };
};

// Query hook - Get all competitionresultentries for dropdowns (cached 4 hours)
export const useCompetitionResultEntries = () => {
  return useQuery({
    queryKey: ['competitionresultentries'],
    queryFn: async () => {
      const response = await apiClient.searchCompetitionResultEntries({
        page: 1,
        pageSize: MAX_DROPDOWN_PAGE_SIZE,
        sortBy: 'Title',
      });
      return response?.data?.results || [];
    },
    staleTime: 4 * 60 * 60 * 1000, // 4 hours
    refetchOnReconnect: true,
  });
};

// Direct API call (no caching) - exports entries matching the given filters to Excel, returns a download URL
export const useExportCompetitionResultEntries = () => {
  const [isLoading, setIsLoading] = useState(false);

  const exportEntries = async (filters?: any): Promise<string | undefined> => {
    setIsLoading(true);
    try {
      const response = await apiClient.exportCompetitionResultEntries({
        page: 1,
        pageSize: 9999999,
        sortBy: 'CreatedUtc',
        sortDescending: true,
        filters: filters || {},
      });
      return response?.data;
    } finally {
      setIsLoading(false);
    }
  };

  return { exportEntries, isLoading };
};

// Direct API call (no caching) - distinct competition years the current member has results for
export const useCompetitionResultEntryDistinctYears = () => {
  const [isLoading, setIsLoading] = useState(false);

  const getDistinctYears = async (): Promise<number[]> => {
    setIsLoading(true);
    try {
      const response = await apiClient.getCompetitionResultEntryDistinctYears();
      return response?.data || [];
    } finally {
      setIsLoading(false);
    }
  };

  return { getDistinctYears, isLoading };
};

// Direct API search (no caching) - for admin tables with server-side paging
export const useCompetitionResultEntrySearch = () => {
  const search = async (params: {
    page: number;
    pageSize: number;
    sortBy?: string;
    sortDescending?: boolean;
    filters?: any;
  }) => {
    const response = await apiClient.searchCompetitionResultEntries({
      page: params.page,
      pageSize: params.pageSize,
      sortBy: params.sortBy || 'CreatedUtc',
      sortDescending: params.sortDescending ?? true,
      filters: params.filters || {},
    });
    return {
      results: response?.data?.results || [],
      totalCount: response?.data?.totalCount || 0,
    };
  };

  return { search };
};

// Query hook - Get single competitionResultEntry by ID
export const useCompetitionResultEntryById = (id: string) => {
  return useQuery({
    queryKey: ['competitionresultentries', id],
    queryFn: async () => {
      const response = await apiClient.getCompetitionResultEntryById(id);
      return response?.data;
    },
    enabled: !!id,
    staleTime: Infinity,
    refetchOnReconnect: true,
  });
};

// Mutation hook - Create competitionResultEntry
export const useCreateCompetitionResultEntry = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CompetitionResultEntryDTO) => {
      await apiClient.createCompetitionResultEntry(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['competitionresultentries'] });
    },
  });
};

// Mutation hook - Update competitionResultEntry
export const useUpdateCompetitionResultEntry = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: CompetitionResultEntryDTO }) => {
      await apiClient.updateCompetitionResultEntry(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['competitionresultentries'] });
    },
  });
};

// Mutation hook - Delete competitionResultEntry
export const useDeleteCompetitionResultEntry = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.deleteCompetitionResultEntry(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['competitionresultentries'] });
    },
  });
};
